import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import Logo from "./Logo";
import { NAV_LINKS } from "../constants/site";
import { analytics } from "../lib/analytics";

export default function Nav({ onNav }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 30);
    fn();
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);

  const go = (href) => {
    setOpen(false);
    setTimeout(() => onNav(href), open ? 250 : 0);
  };

  return (
    <>
      <motion.header
        data-testid="main-header"
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
          scrolled ? "py-3" : "py-5"
        }`}
      >
        <div
          className={`mx-auto max-w-7xl px-4 sm:px-6 lg:px-10 transition-all duration-500 ${
            scrolled ? "glass rounded-2xl shadow-soft" : ""
          }`}
        >
          <div className="flex items-center justify-between py-3 px-2 sm:px-4">
            <button
              data-testid="nav-logo-button"
              onClick={() => window.__lenis?.scrollTo(0, { duration: 1.4 })}
              className="cursor-pointer"
              aria-label="TaccTax home"
            >
              <Logo h={42} />
            </button>

            <nav className="hidden lg:flex items-center gap-8">
              {NAV_LINKS.map((l) => (
                <button
                  key={l.href}
                  data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => go(l.href)}
                  className="relative text-[13px] font-semibold tracking-wide text-slate-600 hover:text-ink transition-colors duration-300 group cursor-pointer"
                >
                  {l.label}
                  <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-gradient-to-r from-teal to-torange transition-all duration-300 group-hover:w-full" />
                </button>
              ))}
            </nav>

            <div className="flex items-center gap-3">
              <button
                data-testid="nav-cta-button"
                onClick={() => { analytics.ctaClick("book_discovery_call", "nav"); go("#contact"); }}
                className="group hidden sm:inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-[13px] font-bold text-white bg-gradient-to-r from-bluedark via-bluze to-torange bg-[length:150%_100%] bg-left hover:bg-right transition-all duration-500 shadow-lift cursor-pointer"
              >
                Book Discovery Call
                <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
              <button
                data-testid="nav-mobile-toggle"
                onClick={() => setOpen(!open)}
                className="lg:hidden inline-flex items-center justify-center w-11 h-11 rounded-xl glass text-ink cursor-pointer"
                aria-label="Toggle menu"
              >
                {open ? <X size={20} /> : <Menu size={20} />}
              </button>
            </div>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-drawer"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-paper/97 backdrop-blur-xl lg:hidden"
          >
            <div className="flex flex-col justify-center h-full px-8 gap-2">
              {NAV_LINKS.map((l, i) => (
                <motion.button
                  key={l.href}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  initial={{ opacity: 0, x: 40 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.07, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                  onClick={() => go(l.href)}
                  className="text-left font-display text-4xl font-bold text-ink py-3 cursor-pointer hover:text-gradient"
                >
                  {l.label}
                </motion.button>
              ))}
              <motion.button
                data-testid="nav-mobile-cta"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                onClick={() => go("#contact")}
                className="mt-6 rounded-full px-6 py-4 text-sm font-bold text-white bg-gradient-to-r from-bluedark to-torange shadow-lift cursor-pointer"
              >
                Book Discovery Call
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
